import { useState } from "react";
import { Stethoscope, Loader2, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { drApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";

function renderText(text) {
  return text.split("\n").map((line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith("**") && p.endsWith("**") ? <strong key={j}>{p.slice(2, -2)}</strong> : <span key={j}>{p}</span>
    );
    return <span key={i} className="block mb-1">{parts}</span>;
  });
}

export default function SecondOpinion() {
  const user = useAuthStore((s) => s.user);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const ask = async () => {
    if (text.trim().length < 10) return toast.error("Please describe your condition, diagnosis or report");
    setLoading(true);
    setResult("");
    try {
      const prompt = `Act as a senior physician giving a careful AI second opinion. Based on the following patient-provided information, explain what it may mean in simple terms, questions to ask the treating doctor, possible next steps and red flags to watch for. Be balanced and non-alarming. Information: "${text.trim()}". End with a clear note that this is not a diagnosis and to consult a qualified doctor.`;
      const res = await drApi.chat({ user_id: user._id, message: prompt, language: "en" });
      setResult(res.data?.response || "Please consult a qualified doctor for a proper opinion.");
    } catch (e) {
      toast.error("Could not get a second opinion. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="second-opinion-page">
      <PageHeader title="AI Second Opinion" subtitle="Understand your diagnosis better" icon={Stethoscope} color="#0EA5E9" />

      <Card className="rounded-3xl p-5 border-border">
        <p className="font-semibold mb-2">Describe your condition or report</p>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. Diagnosed with Type 2 diabetes, HbA1c 8.2, started on Metformin 500mg. Should I be worried?"
          className="rounded-2xl min-h-[140px]"
          data-testid="second-opinion-input"
        />
        <Button onClick={ask} disabled={loading} className="w-full mt-4 h-12 rounded-2xl bg-[#0EA5E9]" data-testid="second-opinion-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get Second Opinion"}
        </Button>
      </Card>

      {result && (
        <Card className="rounded-3xl p-5 border-border mt-5" data-testid="second-opinion-result">
          <p className="font-semibold mb-2 text-[#0EA5E9]">Your AI Second Opinion</p>
          <div className="text-sm text-foreground leading-relaxed">{renderText(result)}</div>
        </Card>
      )}

      <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground mt-4">
        <ShieldCheck className="h-3.5 w-3.5" /> Not a medical diagnosis. Always consult a qualified doctor.
      </p>
    </div>
  );
}
